import React, { createContext, useEffect, useState, useContext } from "react";
import * as signalR from "@microsoft/signalr";
import { getToken } from "../utils/auth";
import CONFIG from "../config";

const AudioContext = createContext();

export const AudioProvider = ({ children }) => {
  const [connection, setConnection] = useState(null);
  const [status, setStatus] = useState({
    isRunning: false,
    currentTrackTitle: null,
    currentArtist: null,
    nextTrackTitle: null,
    nextTrackArtist: null,
    trackElapsed: "00:00:00",
    trackDuration: "00:00:00",
    progressPercent: 0,
    startedAt: null,
    playlistName: null,
    switchPlaylistRequested: false,
    pendingPlaylistName: "",
    playlistElapsed: "00:00:00",
    nextTaskPlaylistName: "",
    nextTaskRemaining: "00:00:00",

  });

  const hubUrl = CONFIG.hubUrl;
  const token = getToken();

  useEffect(() => {
    const connect = async () => {
      try {
        const hubConnection = new signalR.HubConnectionBuilder()
          .withUrl(hubUrl, {
            accessTokenFactory: () => token
          })
          .withAutomaticReconnect()
          .build();

        hubConnection.on("AudioStatusUpdate", (newStatus) => {
          // Update state directly with backend status object
          setStatus({
            isRunning: newStatus.isRunning,
            currentTrackTitle: newStatus.currentTrackTitle,
            currentArtist: newStatus.currentArtist,
            nextTrackTitle: newStatus.nextTrackTitle,
            nextTrackArtist: newStatus.nextTrackArtist,
            trackElapsed: newStatus.trackElapsed,
            trackDuration: newStatus.trackDuration,
            progressPercent: newStatus.progressPercent,
            startedAt: newStatus.startedAt,
            playlistName: newStatus.playlistName,
            switchPlaylistRequested: newStatus.switchPlaylistRequested,
            pendingPlaylistName: newStatus.pendingPlaylistName,
            playlistElapsed: newStatus.playlistElapsed,
            nextTaskPlaylistName: newStatus.nextTaskPlaylistName,
            nextTaskRemaining: newStatus.nextTaskRemaining,
          });
        });

        await hubConnection.start();
        console.log("✅ Connected to AudioHub");
        setConnection(hubConnection);

        const currentStatus = await hubConnection.invoke("GetStatus");
        setStatus(currentStatus);
      } catch (err) {
        console.error("❌ SignalR connection failed:", err);
      }
    };

    connect();

    return () => {
      if (connection) {
        connection.stop();
      }
    };
  }, []);

  const startEngine = async () => {
    if (!connection) return;
    await connection.invoke("StartAudio");
  };

  const stopEngine = async () => {
    if (!connection) return;
    await connection.invoke("StopAudio");
  };

  const manuallySwitchPlaylist = async (playlistName) => {
    if (!connection) return;
    try {
      await connection.invoke("ManuallySwitchPlaylist", playlistName);
      console.log(`Switched to playlist: ${playlistName}`);
    } catch (err) {
      console.error("Failed to switch playlist:", err);
    }
  };

  const getStatus = async () => {
    if (!connection) return;
    const currentStatus = await connection.invoke("GetStatus");
    setStatus(currentStatus);
    return currentStatus;
  };

  return (
    <AudioContext.Provider value={{ status, startEngine, stopEngine, getStatus, manuallySwitchPlaylist }}>
      {children}
    </AudioContext.Provider>
  );
};

export const useAudio = () => useContext(AudioContext);